import { useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore'
import { JOBS, fleetHealth, type JobHealth, type JobRun } from '@racedash/shared'
import { db } from '../lib/firebase'

/**
 * Everything the operations view reads.
 *
 * All of it live, because the page exists to be left open on a race morning
 * and a status view you have to refresh is a status view that lies.
 *
 * Note what is NOT here: any attempt to work out what should be capturing.
 * That is the reconciler's conclusion, read from ops/capturePlan. Recomputing
 * it here would mean a second implementation of window construction — timezone
 * aware, attachment aware, chunked — drifting from the Go one, and being
 * confidently wrong about the only question the page is asked.
 */

/** Firestore hands back Timestamps; nothing downstream should have to care. */
type Stamped = { toDate(): Date } | Timestamp | null | undefined

function asDate(v: Stamped): Date | null {
  if (!v || typeof (v as { toDate?: unknown }).toDate !== 'function') return null
  return (v as Timestamp).toDate()
}

// ------------------------------------------------------------------ jobs

/**
 * The most recent run of every declared job.
 *
 * One listener per job rather than one over the whole collection, because the
 * question is "what did each of these last do" and the collection is dominated
 * by the reconciler's 288 runs a day — a single ordered listener would spend
 * its limit on those and never reach the nightly scrape.
 */
export function useLatestJobRuns() {
  const [runs, setRuns] = useState<Map<string, JobRun>>(new Map())

  useEffect(() => {
    const unsubs = JOBS.map((spec) =>
      onSnapshot(
        query(
          collection(db, 'jobRuns'),
          where('job', '==', spec.id),
          orderBy('startedAt', 'desc'),
          limit(1),
        ),
        (snap) => {
          const d = snap.docs[0]
          setRuns((prev) => {
            const next = new Map(prev)
            if (d) next.set(spec.id, toJobRun(d.id, d.data()))
            else next.delete(spec.id)
            return next
          })
        },
        // Swallowed deliberately: a job whose runs cannot be read is reported
        // by fleetHealth as never having run, which is the safe reading.
        () => {},
      ),
    )
    return () => unsubs.forEach((u) => u())
  }, [])

  return runs
}

/** Recent history for one job, for the "has this been happening" question. */
export function useJobHistory(job: string, max = 20) {
  const [runs, setRuns] = useState<JobRun[] | null>(null)

  useEffect(() => {
    setRuns(null)
    return onSnapshot(
      query(
        collection(db, 'jobRuns'),
        where('job', '==', job),
        orderBy('startedAt', 'desc'),
        limit(max),
      ),
      (snap) => setRuns(snap.docs.map((d) => toJobRun(d.id, d.data()))),
      () => setRuns([]),
    )
  }, [job, max])

  return runs
}

/** The runs belonging to one capture window — its two halves, and their counters. */
export function useWindowRuns(windowId: string | null) {
  const [runs, setRuns] = useState<JobRun[]>([])

  useEffect(() => {
    if (!windowId) {
      setRuns([])
      return
    }
    return onSnapshot(
      query(
        collection(db, 'jobRuns'),
        where('windowId', '==', windowId),
        orderBy('startedAt', 'desc'),
        limit(10),
      ),
      (snap) => setRuns(snap.docs.map((d) => toJobRun(d.id, d.data()))),
      () => setRuns([]),
    )
  }, [windowId])

  return runs
}

function toJobRun(id: string, d: Record<string, unknown>): JobRun {
  const started = asDate(d.startedAt as Stamped) ?? new Date(0)
  return {
    id,
    job: (d.job as string) ?? '',
    execution: (d.execution as string) ?? id,
    windowId: (d.windowId as string) || undefined,
    status: ((d.status as string) ?? 'running') as JobRun['status'],
    startedAt: started,
    // A run written before its first beat has none; treating that as the start
    // is right, and treating it as the epoch would read as instantly stalled.
    heartbeatAt: asDate(d.heartbeatAt as Stamped) ?? started,
    finishedAt: asDate(d.finishedAt as Stamped),
    summary: (d.summary as string) || undefined,
    error: (d.error as string) || undefined,
    build: (d.build as string) || undefined,
    counters: (d.counters as Record<string, number>) ?? {},
    notes: (d.notes as string[]) ?? [],
  }
}

// ------------------------------------------------------------------ plan

export interface PlannedWindow {
  id: string
  eventId: string
  eventName: string
  trackId: string
  channels: string[]
  start: Date
  end: Date
  attached: string[]
  actionable: boolean
}

export interface PlannedSkip {
  eventId: string
  eventName: string
  reason: string
  detail: string
  attached: string[]
  startsAt: Date | null
  endsAt: Date | null
}

export interface CapturePlan {
  generatedAt: Date | null
  horizonDays: number
  windows: PlannedWindow[]
  skips: PlannedSkip[]
  truncated: boolean
}

/**
 * The reconciler's forward plan.
 *
 * null while loading; a plan with no generatedAt means the document does not
 * exist, which is itself a finding — the reconciler has never published one.
 */
export function useCapturePlan() {
  const [plan, setPlan] = useState<CapturePlan | null>(null)

  useEffect(() => {
    return onSnapshot(
      doc(db, 'ops', 'capturePlan'),
      (snap) => {
        const d = snap.data()
        if (!d) {
          setPlan({ generatedAt: null, horizonDays: 0, windows: [], skips: [], truncated: false })
          return
        }
        setPlan({
          generatedAt: asDate(d.generatedAt as Stamped),
          horizonDays: (d.horizonDays as number) ?? 0,
          truncated: Boolean(d.truncated),
          windows: ((d.windows as Record<string, unknown>[]) ?? []).map((w) => ({
            id: (w.id as string) ?? '',
            eventId: (w.eventId as string) ?? '',
            eventName: (w.eventName as string) ?? '',
            trackId: (w.trackId as string) ?? '',
            channels: (w.channels as string[]) ?? [],
            start: asDate(w.start as Stamped) ?? new Date(0),
            end: asDate(w.end as Stamped) ?? new Date(0),
            attached: (w.attached as string[]) ?? [],
            actionable: Boolean(w.actionable),
          })),
          skips: ((d.skips as Record<string, unknown>[]) ?? []).map((s) => ({
            eventId: (s.eventId as string) ?? '',
            eventName: (s.eventName as string) ?? '',
            reason: (s.reason as string) ?? '',
            detail: (s.detail as string) ?? '',
            attached: (s.attached as string[]) ?? [],
            startsAt: asDate(s.startsAt as Stamped),
            endsAt: asDate(s.endsAt as Stamped),
          })),
        })
      },
      () => setPlan({ generatedAt: null, horizonDays: 0, windows: [], skips: [], truncated: false }),
    )
  }, [])

  return plan
}

// ---------------------------------------------------------- capture runs

export interface CaptureRun {
  id: string
  status: 'running' | 'finished' | 'abandoned' | string
  eventId: string
  eventName: string
  trackId: string
  windowStart: Date | null
  windowEnd: Date | null
  attached: string[]
  attempts: number
  execution: string
  interpretExecution: string
  startedAt: Date | null
  lastSeenAt: Date | null
  note?: string
}

export function useCaptureRuns(max = 25) {
  const [runs, setRuns] = useState<CaptureRun[] | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'captureRuns'), orderBy('windowStart', 'desc'), limit(max)),
      (snap) =>
        setRuns(
          snap.docs.map((d) => {
            const v = d.data()
            return {
              id: d.id,
              status: (v.status as string) ?? 'unknown',
              eventId: (v.eventId as string) ?? '',
              eventName: (v.eventName as string) ?? '',
              trackId: (v.trackId as string) ?? '',
              windowStart: asDate(v.windowStart as Stamped),
              windowEnd: asDate(v.windowEnd as Stamped),
              attached: (v.attached as string[]) ?? [],
              attempts: (v.attempts as number) ?? 0,
              execution: (v.execution as string) ?? '',
              interpretExecution: (v.interpretExecution as string) ?? '',
              startedAt: asDate(v.startedAt as Stamped),
              lastSeenAt: asDate(v.lastSeenAt as Stamped),
              note: (v.note as string) || undefined,
            }
          }),
        ),
      () => setRuns([]),
    )
  }, [max])

  return runs
}

// --------------------------------------------------------------- alerts

export interface SentAlert {
  key: string
  subject: string
  body: string
  lastSentAt: Date | null
}

/**
 * What has actually been emailed.
 *
 * The cooldown claim doubles as the record, so this is the only place an alert
 * can be read back once the message has been dealt with — and it is worth
 * showing beside the current state, because an alert that fired and then
 * resolved is a different story from one that is still true.
 */
export function useSentAlerts(max = 20) {
  const [alerts, setAlerts] = useState<SentAlert[] | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'alerts'), orderBy('lastSentAt', 'desc'), limit(max)),
      (snap) =>
        setAlerts(
          snap.docs.map((d) => {
            const v = d.data()
            return {
              key: (v.key as string) ?? d.id,
              // Documents written before the subject was stored held the key
              // in this field. Showing the id is no worse than showing an
              // identifier labelled as a subject.
              subject: (v.subject as string) ?? d.id,
              body: (v.body as string) ?? '',
              lastSentAt: asDate(v.lastSentAt as Stamped),
            }
          }),
        ),
      () => setAlerts([]),
    )
  }, [max])

  return alerts
}

// -------------------------------------------------------- RIS channels

export interface RisChannel {
  id: string
  path: string
  seriesName: string
  raceName: string
  sessionName: string
  status: string
  cars: number
  generatedAt: Date | null
  observedAt: Date | null
  fresh: boolean
  error?: string
  throttled: boolean
}

export function useRisChannels() {
  const [channels, setChannels] = useState<RisChannel[] | null>(null)

  useEffect(() => {
    return onSnapshot(
      collection(db, 'risChannels'),
      (snap) =>
        setChannels(
          snap.docs.map((d) => {
            const v = d.data()
            return {
              id: d.id,
              path: (v.path as string) ?? '',
              seriesName: (v.seriesName as string) ?? '',
              raceName: (v.raceName as string) ?? '',
              sessionName: (v.sessionName as string) ?? '',
              status: (v.status as string) ?? '',
              cars: (v.cars as number) ?? 0,
              generatedAt: asDate(v.generatedAt as Stamped),
              observedAt: asDate(v.observedAt as Stamped),
              fresh: Boolean(v.fresh),
              error: (v.error as string) || undefined,
              throttled: Boolean(v.throttled),
            }
          }),
        ),
      () => setChannels([]),
    )
  }, [])

  return channels
}

// --------------------------------------------------------------- verdict

export interface OpsHealth {
  jobs: JobHealth[]
  /** Anything a person should act on, already phrased for display. */
  problems: string[]
  /** Windows whose time is now, whether or not anything is running for them. */
  openWindows: PlannedWindow[]
  plan: CapturePlan | null
  /** The one line at the top of the page, and the tooltip on the header dot. */
  verdict: string
  tone: 'ok' | 'busy' | 'warn'
}

/**
 * The single judgement the page and the header indicator both render.
 *
 * Shared rather than computed twice, because the two disagreeing is worse than
 * either being wrong: a green dot over a page listing three failures teaches
 * the reader that the dot means nothing.
 */
export function useOpsHealth(now: Date): OpsHealth {
  const runs = useLatestJobRuns()
  const plan = useCapturePlan()
  const deployed = useDeployedBuild()

  return useMemo(() => {
    const jobs = fleetHealth(runs, now, undefined, deployed?.build)
    const problems: string[] = []

    for (const h of jobs) {
      if (h.bad) problems.push(`${h.spec.label}: ${h.detail}`)
    }
    for (const skip of plan?.skips ?? []) {
      problems.push(skip.detail)
    }

    const openWindows = (plan?.windows ?? []).filter((w) => w.start <= now && now < w.end)

    // A plan that has never been published is its own finding, and a silent one
    // — the page would otherwise show an empty "nothing coming up", which is
    // exactly what a healthy quiet week looks like.
    if (plan && !plan.generatedAt) {
      problems.push('The reconciler has never published a plan, so nothing here is known to be current.')
    }

    let verdict: string
    let tone: OpsHealth['tone']
    if (problems.length > 0) {
      verdict = problems.length === 1 ? '1 problem needs attention' : `${problems.length} problems need attention`
      tone = 'warn'
    } else if (openWindows.length > 0) {
      const names = [...new Set(openWindows.map((w) => w.eventName))]
      verdict = `Capturing ${names.join(', ')}`
      tone = 'busy'
    } else {
      verdict = 'Idle, and everything is reporting in'
      tone = 'ok'
    }

    return { jobs, problems, openWindows, plan, verdict, tone }
  }, [runs, plan, now, deployed])
}

/**
 * A clock that ticks, so "last seen 4 minutes ago" ages on screen.
 *
 * Without it the page is only as fresh as its last Firestore change, and the
 * one state that produces no changes at all is the one worth noticing.
 */
export function useNow(everyMs = 15_000) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs)
    return () => clearInterval(t)
  }, [everyMs])
  return now
}

export interface DeployedBuild {
  build: string
  image?: string
  deployedAt?: Date | null
}

/**
 * What the last deploy put in the timing image.
 *
 * The counterpart to the build each run records. A Cloud Run Job pins its image
 * when the execution starts, so one that began before a deploy keeps running
 * the previous build until it ends — invisible, and twice the reason a deployed
 * fix looked as though it had not worked.
 *
 * A listener rather than a fetch, because a deploy can land while this page is
 * open: that is precisely the moment somebody is watching to see whether it
 * took.
 */
export function useDeployedBuild(): DeployedBuild | null {
  const [row, setRow] = useState<DeployedBuild | null>(null)

  useEffect(
    () =>
      onSnapshot(
        doc(db, 'ops', 'timingImage'),
        (snap) => {
          const d = snap.data()
          setRow(
            d?.build
              ? {
                  build: String(d.build),
                  image: typeof d.image === 'string' ? d.image : undefined,
                  deployedAt: d.deployedAt?.toDate?.() ?? null,
                }
              : null,
          )
        },
        // Swallowed like the rest of this file: not knowing which build is
        // current means no stale-build notes, which is where this started.
        () => setRow(null),
      ),
    [],
  )

  return row
}
